import { useCallback, useEffect, useRef, useState } from "react";
import * as Schema from "effect/Schema";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { openConnectionConsent } from "./connectionConsent";

const Snapshot = Schema.Struct({
  /** Whether this server can broker Composio workspace OAuth at all. */
  connectable: Schema.Boolean,
  /**
   * Per partner-OAuth integration (Altruist): whether this server holds the
   * vendor's partner credentials, so a sign-in can start. False reads as
   * "awaiting partner access". Absent on an older server.
   */
  partnerConnectable: Schema.optional(Schema.Record(Schema.String, Schema.Boolean)),
  accounts: Schema.Array(
    Schema.Struct({
      connectorId: Schema.String,
      status: Schema.Literals(["connected", "disconnected", "pending", "error"]),
      enabled: Schema.Boolean,
      accountLabel: Schema.NullOr(Schema.String),
    }),
  ),
});
export type WorkspaceOAuthSnapshot = typeof Snapshot.Type;
type Snapshot = WorkspaceOAuthSnapshot;

type Contract = "oauth" | "api-key" | "partner-oauth" | undefined;

/** Whether an integration connects through the workspace sign-in flow (Composio or partner OAuth). */
export const usesWorkspaceOAuth = (contract: Contract): boolean =>
  contract === undefined || contract === "oauth" || contract === "partner-oauth";

/**
 * Whether a sign-in can start for this integration from this server, as the
 * snapshot says: Composio's broker for OAuth ones, the vendor's partner
 * credentials for partner-OAuth ones. Null while the snapshot is unknown.
 */
export function workspaceOAuthAvailable(
  snapshot: Snapshot | null,
  integration: { readonly id: string; readonly contract?: Contract },
): boolean | null {
  if (snapshot === null) return null;
  if (integration.contract === "partner-oauth") {
    return snapshot.partnerConnectable?.[integration.id] === true;
  }
  return snapshot.connectable;
}
export function workspaceOAuthUrl(
  organizationId: string,
  action?: "connect" | "reconnect" | "disconnect",
) {
  return resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/connections${action ? `/${action}` : ""}`, {
    org: organizationId,
    scope: "workspace",
  });
}
export async function postWorkspaceOAuthAction(
  organizationId: string,
  connectorId: string,
  action: "connect" | "reconnect" | "disconnect",
) {
  const url = new URL(workspaceOAuthUrl(organizationId, action));
  url.searchParams.set("connection", connectorId);
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    signal: AbortSignal.timeout(30_000),
  });
  if (!response.ok)
    throw new Error(
      "The workspace connection could not be changed. Check admin access and try again.",
    );
  return response;
}

export function useWorkspaceIntegrationOAuth(organizationId: string | null) {
  const [state, setState] = useState<{ org: string; snapshot: Snapshot } | null>(null);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const currentOrg = useRef(organizationId);
  currentOrg.current = organizationId;
  const snapshot = state?.org === organizationId ? state.snapshot : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: revision explicitly invalidates the current request after an action or focus.
  useEffect(() => {
    if (!organizationId) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let active = true;
    const deadline = Date.now() + 11 * 60_000;
    const read = async () => {
      try {
        const response = await fetch(workspaceOAuthUrl(organizationId), {
          credentials: "include",
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(20_000)]),
        });
        if (!response.ok) return;
        const next = Schema.decodeUnknownSync(Snapshot)(await response.json());
        if (!active) return;
        setState({ org: organizationId, snapshot: next });
        if (next.accounts.some((account) => account.status === "pending") && Date.now() < deadline)
          timer = setTimeout(() => {
            if (document.visibilityState === "visible") void read();
          }, 3000);
      } catch {
        /* Keep the last known state; focus or an action retries. */
      }
    };
    void read();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [organizationId, revision]);
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);
  const runAction = useCallback(
    async (
      connectorId: string,
      action: "connect" | "reconnect" | "disconnect",
    ): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      const request = () => postWorkspaceOAuthAction(organizationId, connectorId, action);
      try {
        if (action === "disconnect") await request();
        else
          await openConnectionConsent({
            openTab: () => window.open("about:blank", "_blank"),
            getUrl: async () => {
              const response = await request();
              const data = Schema.decodeUnknownSync(Schema.Struct({ redirectUrl: Schema.String }))(
                await response.json(),
              );
              return data.redirectUrl;
            },
            navigate: (url) => {
              window.location.href = url;
            },
          });
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : "The workspace connection failed.";
      } finally {
        if (currentOrg.current === organizationId) refresh();
      }
    },
    [organizationId, refresh],
  );
  return { snapshot, refresh, runAction };
}
